import { lighten, darken } from '@mui/material/styles';
import type { Palette } from '@mui/material/styles';
import {
    computeComponentColors,
    getContrastingVariant,
    type ComponentColorResult,
} from '../../hooks/useThemeContrastColor';

// ─────────────────────────────────────────────────────────────────────────────
// Palette keys — declared first so derived types can reference them
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Colors always available in the base flexy-components theme.
 */
export const CORE_PALETTE_KEYS = [
    'primary',
    'secondary',
    'warning',
    'success',
    'error',
    'info',
    'danger',
    'cardboard',
] as const;

/**
 * Colors added by app-level themes (e.g. l2d-modules).
 * Not present at BuildRawTheme time — injected in a second pass by the app.
 */
export const EXTENDED_PALETTE_KEYS = [
    'purple',
    'amber',
    'blueGrey',
    'deepOrange',
    'teal',
    'red',
    'pink',
    'lightGreen',
    'cyan',
    'green',
    'indigo',
    'lightBlue',
    'lime',
    'orange',
    'yellow',
    'blue',
    'brown',
    'deepPurple',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type CorePaletteKey = (typeof CORE_PALETTE_KEYS)[number];
export type ExtendedPaletteKey = (typeof EXTENDED_PALETTE_KEYS)[number];
export type PaletteKey = CorePaletteKey | ExtendedPaletteKey;

/**
 * Type universel pour tous les tokens de composants.
 *
 * Le paramètre générique `K` contraint les clés autorisées, ce qui active
 * l'autocomplétion TypeScript sur `palette.chip['…']`, `palette.actionButton['…']`, etc.
 * Par défaut `string` pour les usages internes non contraints.
 */
export type ComponentTokenColorMap<K extends string = string> = Partial<
    Record<K, ComponentColorResult>
>;

// ─── Clés sémantiques par token ───────────────────────────────────────────────

export type ActionButtonKey = 'primary' | 'secondary' | 'error' | 'warning';
export type StatusKey =
    | 'formalist'
    | 'formalistAction'
    | 'validator'
    | 'client'
    | 'clientAction'
    | 'success'
    | 'danger'
    | 'inactive';
export type CrudActionKey =
    | 'add'
    | 'edit'
    | 'view'
    | 'download'
    | 'downloadSigned'
    | 'upload'
    | 'sign'
    | 'move'
    | 'validate'
    | 'delete'
    | 'confirmation';
export type ActivityTimelineItemKey = 'actionIcon';
export type NoteKey = 'default' | 'important';

export type ComponentTokensShape = {
    /** Colors keyed by palette color name — chips, badges */
    chip: ComponentTokenColorMap<PaletteKey>;
    /** Colors keyed by palette color name — icon containers */
    icon: ComponentTokenColorMap<PaletteKey>;
    /** Colors keyed by palette color name — generic colored elements */
    button: ComponentTokenColorMap<PaletteKey>;
    /** Colors keyed by palette color name — checkbox checked/indeterminate icon */
    checkbox: ComponentTokenColorMap<PaletteKey>;
    /** Colors keyed by CTA variant name: primary / secondary / error / warning */
    actionButton: ComponentTokenColorMap<ActionButtonKey>;
    /** Colors keyed by workflow status name: formalist / validator / client… */
    status: ComponentTokenColorMap<StatusKey>;
    /** Colors for ActivityTimeline items (action icon circle, etc.) */
    activityTimelineItem: ComponentTokenColorMap<ActivityTimelineItemKey>;
    /** Colors for note items — keys: default / important */
    note: ComponentTokenColorMap<NoteKey>;
    /** Colors keyed by CRUD action name: add / edit / delete / … */
    crudAction: ComponentTokenColorMap<CrudActionKey>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Fallback (used when a key has no matching color in the map yet)
// ─────────────────────────────────────────────────────────────────────────────

const FALLBACK_TOKEN: ComponentColorResult = {
    backgroundColor: '#f5f5f5',
    backgroundColorOnHover: '#eeeeee',
    backgroundGradient: 'linear-gradient(135deg, #f5f5f5, #eeeeee)',
    backgroundGradientOnHover: 'linear-gradient(135deg, #eeeeee, #e0e0e0)',
    glassyStyle: { background: 'linear-gradient(135deg, #f5f5f5, #eeeeee)' },
    glassyStyleOnHover: { background: 'linear-gradient(135deg, #eeeeee, #e0e0e0)' },
    textColor: '#424242',
    textColorOnHover: '#212121',
};

// ─────────────────────────────────────────────────────────────────────────────
// MUI augmentation
// ─────────────────────────────────────────────────────────────────────────────

declare module '@mui/material/styles' {
    interface Palette {
        chip: ComponentTokenColorMap<PaletteKey>;
        icon: ComponentTokenColorMap<PaletteKey>;
        button: ComponentTokenColorMap<PaletteKey>;
        checkbox: ComponentTokenColorMap<PaletteKey>;
        actionButton: ComponentTokenColorMap<ActionButtonKey>;
        status: ComponentTokenColorMap<StatusKey>;
        activityTimelineItem: ComponentTokenColorMap<ActivityTimelineItemKey>;
        note: ComponentTokenColorMap<NoteKey>;
        crudAction: ComponentTokenColorMap<CrudActionKey>;
    }
    interface PaletteOptions {
        chip?: ComponentTokenColorMap<PaletteKey>;
        icon?: ComponentTokenColorMap<PaletteKey>;
        button?: ComponentTokenColorMap<PaletteKey>;
        checkbox?: ComponentTokenColorMap<PaletteKey>;
        actionButton?: ComponentTokenColorMap<ActionButtonKey>;
        status?: ComponentTokenColorMap<StatusKey>;
        activityTimelineItem?: ComponentTokenColorMap<ActivityTimelineItemKey>;
        note?: ComponentTokenColorMap<NoteKey>;
        crudAction?: ComponentTokenColorMap<CrudActionKey>;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds a `ComponentTokensShape` from a resolved MUI palette.
 *
 * @param palette - A fully-resolved MUI `Palette` (output of `createTheme`)
 * @param keys    - Palette key names to include in chip/icon/button; defaults to `CORE_PALETTE_KEYS`
 */
export const buildComponentTokens = (
    palette: Palette,
    keys: readonly PaletteKey[] = CORE_PALETTE_KEYS,
): ComponentTokensShape => {
    const { mode } = palette;

    // ── chip / icon / button ─────────────────────────────────────────────────
    const colorMap: ComponentTokenColorMap<PaletteKey> = {};

    keys.forEach((key) => {
        const entry = (palette as unknown as Record<string, unknown>)[key];
        if (entry && typeof entry === 'object' && 'main' in (entry as object)) {
            colorMap[key] = computeComponentColors((entry as { main: string }).main, mode);
        }
    });

    const fromMap = (key: PaletteKey): ComponentColorResult => colorMap[key] ?? FALLBACK_TOKEN;

    // ── status ───────────────────────────────────────────────────────────────
    const status: ComponentTokenColorMap<StatusKey> = {
        formalist: fromMap('primary'),
        formalistAction: fromMap('deepOrange'),
        validator: fromMap('purple'),
        client: fromMap('primary'),
        clientAction: fromMap('amber'),
        success: fromMap('success'),
        danger: fromMap('error'),
        inactive: fromMap('blueGrey'),
    };

    // ── actionButton ─────────────────────────────────────────────────────────
    const makeToken = (
        bg: string,
        bgHover: string,
        text: string,
        textHover: string,
    ): ComponentColorResult => ({
        backgroundColor: bg,
        backgroundColorOnHover: bgHover,
        backgroundGradient: bg,
        backgroundGradientOnHover: bgHover,
        glassyStyle: { background: bg },
        glassyStyleOnHover: { background: bgHover },
        textColor: text,
        textColorOnHover: textHover,
    });

    const actionButton: ComponentTokenColorMap<ActionButtonKey> = {
        primary: makeToken(
            palette.primary.main,
            palette.primary.light,
            palette.background.paper,
            palette.background.paper,
        ),
        secondary: makeToken(
            palette.grey[200],
            palette.grey[300],
            palette.text.primary,
            palette.text.primary,
        ),
        error: makeToken(
            palette.error.main,
            palette.error.light,
            palette.getContrastText(palette.error.main),
            palette.getContrastText(palette.error.light),
        ),
        warning: makeToken(
            palette.warning.light,
            palette.warning.main,
            palette.getContrastText(palette.warning.light),
            palette.getContrastText(palette.warning.main),
        ),
    };

    // ── activityTimelineItem ─────────────────────────────────────────────────
    const activityTimelineItem: ComponentTokenColorMap<ActivityTimelineItemKey> = {
        actionIcon: computeComponentColors(palette.secondary.main, mode),
    };

    // ── note ─────────────────────────────────────────────────────────────────
    const importantNoteBg: string =
        mode === 'light' ? lighten(palette.primary.main, 0.8) : darken(palette.primary.main, 0.5);

    const note: ComponentTokenColorMap<NoteKey> = {
        default: makeToken(
            palette.background.default,
            palette.action.selected,
            palette.text.primary,
            palette.text.primary,
        ),
        important: makeToken(
            importantNoteBg,
            palette.primary.main,
            getContrastingVariant(palette.primary.main, importantNoteBg, 3, mode),
            palette.getContrastText(palette.primary.main),
        ),
    };

    // ── crudAction ───────────────────────────────────────────────────────────
    // Standard semantic colors per action. The component (ItemActions) owns the
    // visual strategy: transparent bg at rest, colored hover via backgroundColorOnHover.
    const crudAction: ComponentTokenColorMap<CrudActionKey> = {
        add: fromMap('primary'),
        edit: fromMap('primary'),
        view: fromMap('info'),
        download: fromMap('primary'),
        downloadSigned: fromMap('success'),
        upload: fromMap('primary'),
        sign: fromMap('info'),
        move: fromMap('secondary'),
        validate: fromMap('success'),
        delete: fromMap('error'),
        confirmation: fromMap('warning'),
    };

    return {
        chip: colorMap,
        icon: colorMap,
        button: colorMap,
        checkbox: colorMap,
        actionButton,
        status,
        activityTimelineItem,
        note,
        crudAction,
    };
};
